import { DataTable } from "@/components/molecules/tables/data-table";
import { useSearchSessions } from "@/features/sessions";
import { useTranslation } from "@/features/utils/i18n";
import { useState } from "react";
import { useCallsColumns } from "./columns";
import { FilterMenuPill } from "@/components/molecules/filter-pills";
import { useClientLinks } from "@/features/clients/hooks/use-scoped-client";

interface CallsTableProps {
  customerId?: string;
  limit?: number;
  hideSearch?: boolean;
  hidePagination?: boolean;
  variant?: React.ComponentProps<typeof DataTable>["variant"];
}

export function CallsTable({
  customerId,
  limit,
  hideSearch,
  hidePagination,
  variant,
}: CallsTableProps) {
  const { t } = useTranslation("calls");
  const links = useClientLinks();
  const columns = useCallsColumns({ hideCustomer: !!customerId });
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, unknown>>({});
  const [state, setState] = useState<"open" | "closed" | undefined>(undefined);
  const [type, setType] = useState<
    "inbound" | "outbound" | "notifications" | undefined
  >(undefined);
  const [medium, setMedium] = useState<"call" | "chat" | "email" | undefined>(
    undefined,
  );

  // Handle search with automatic filter extraction
  const handleSearchChange = (
    searchText: string,
    extractedFilters?: Record<string, unknown>,
  ) => {
    setSearch(searchText);

    // Merge extracted filters
    if (extractedFilters && Object.keys(extractedFilters).length > 0) {
      setFilters((prev) => {
        const newFilters = { ...prev };

        Object.entries(extractedFilters).forEach(([key, value]) => {
          if (
            typeof value === "object" &&
            value !== null &&
            "operator" in value &&
            "value" in value
          ) {
            const filterValue = (value as { operator: string; value: unknown })
              .value;
            if ((value as { operator: string }).operator === "eq") {
              newFilters[key] = filterValue;
            }
          } else {
            newFilters[key] = value;
          }
        });

        return newFilters;
      });
    }
  };

  const { data, isLoading } = useSearchSessions({
    ...filters,
    customer_id: customerId,
    state,
    type,
    medium,
    search: search || undefined,
    limit: limit || 50,
    sort_by: "last_message_at",
    sort_order: "desc",
  });

  return (
    <DataTable
      hidePagination={hidePagination}
      hideSearch={hideSearch}
      variant={variant}
      columns={columns}
      data={data?.data || []}
      onRowNavigate={(session) => links.call(session.id)}
      stickyColumnsCount={0}
      isLoading={isLoading}
      total={data?.total || 0}
      globalFilter={search}
      setGlobalFilter={handleSearchChange}
      searchBar={(searchBar) => (
        <div className="flex flex-1 flex-wrap gap-2">
          {searchBar}
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterMenuPill
              label={t("filters.type")}
              clearLabel={t("filters.clear", { name: t("filters.type") })}
              value={type}
              onChange={setType}
              options={[
                { value: "inbound", label: t("types.inbound") },
                { value: "outbound", label: t("types.outbound") },
                { value: "notifications", label: t("types.notifications") },
              ]}
              allLabel={t("types.all")}
            />
            <FilterMenuPill
              label={t("filters.medium")}
              clearLabel={t("filters.clear", { name: t("filters.medium") })}
              value={medium}
              onChange={setMedium}
              options={[
                { value: "call", label: t("mediums.call") },
                { value: "chat", label: t("mediums.chat") },
                { value: "email", label: t("mediums.email") },
              ]}
              allLabel={t("mediums.all")}
            />
            <FilterMenuPill
              label={t("filters.status")}
              clearLabel={t("filters.clear", { name: t("filters.status") })}
              value={state}
              onChange={setState}
              options={[
                { value: "open", label: t("states.open") },
                { value: "closed", label: t("states.closed") },
              ]}
              allLabel={t("states.all")}
            />
          </div>
        </div>
      )}
    />
  );
}
